import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ReactComponent as WarningImage } from '../../../assets/delete-img-modal.svg';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import RowTypography from 'components/Table/RowTypography';
import RowChip from '../../Table/RowChip';
import { useTheme } from "@mui/material/styles";
import fontSizeResponsive from 'assets/theme/functions/fontSizeResponsive';

type ModalWidth = 'sm' | 'md' | 'lg' | 'xl';

interface FormData {
	PepwiseProgram: string;
	PepwiseProject: string;
	PepwiseSector: string;
	PepwiseEnvironment: string;
    [key: string]: any; 
	// DNAI_Value: string;
}

interface BulkUpdateConflictsModalProps {
	onTitleChange: (newtitle: string) => void;
	onWidthChange: (newWidth: ModalWidth) => void;
	onCancel: () => void;
    handleSave: (
        bulkSelectedRows: Record<string, any>, // Improved type: use Record for better structure
        override: 'N' | 'Y',
        skipconflict: 'N' | 'Y'
      ) => void;
    bulkEditResponse: any;
    bulkSelectedRows: any;
	queryPreference: any;
    formData: FormData;
	allocationType: {
		key: string;
		label: string;
	};
}

const pepwiseFormatter = (isAfterUpdate: boolean, formData: any, colors: any) => {
    return ({ row, column }: any) => {
        if (isAfterUpdate) {
            return renderAfterUpdateCell(row, column, formData);
        }

        // BEFORE update
        const beforeCell = renderBeforeUpdateCell(row, column, colors);
        if (beforeCell) {
            return beforeCell;
        }

        return (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
				<RowTypography isSuccessor={!row[column.key]}>{row[column.key] || ''}</RowTypography>
			</Stack>
        );
    };
};

const renderPepwiseChip = (row: any, columnKey: string, colors: any) => {
    const map: Record<string, { countKey: string; label: string }> = {
        PepwiseSector: { countKey: 'SectorCount', label: 'SECTORS' },
        PepwiseProject: { countKey: 'ProjectCount', label: 'PROJECTS' },
        PepwiseProgram: { countKey: 'ProgramCount', label: 'PROGRAMS' }
    };

    const config = map[columnKey];
    if (!config) return null;

    const count = row[config.countKey];
    const value = count > 1 ? `${count} ${config.label}` : row[columnKey];

    if (!value) return null;

    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <RowChip value={value} rowId={row.id} bgColor={colors.table.chipBackground} color={colors.table.chipText}/>
        </Stack>
    );
};

const renderBeforeUpdateCell = (row: any, column: any, colors: any) => {

    if (column.key === 'AllocPercent') {
        return (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
				<RowTypography isSuccessor={!row[column.key]}>
					{row[column.key] ? `${row[column.key]}%` : ''}
				</RowTypography>
			</Stack>
        );
    }

    if (['PepwiseSector', 'PepwiseProject', 'PepwiseProgram'].includes(column.key)) {
        return renderPepwiseChip(row, column.key, colors);
    }

    return null;
};

const renderAfterUpdateCell = (row: any, column: any, formData: any) => {

    if (column.key === 'AllocPercent') {
        return (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
				<RowTypography isSuccessor={!row[column.key]}>{'100%'}</RowTypography>
			</Stack>
        );
    }

    if (['PepwiseSector', 'PepwiseProject', 'PepwiseProgram'].includes(column.key)) {
        if (formData[column.key]) {
            return (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <RowChip value={formData[column.key] ? formData[column.key] : ''} rowId={row.id} bgColor="#299BFF26" color="#003771"/>
                </Stack>
            );
        }
    }

    if (column.key === 'PepwiseEnvironment') {
        return (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
				<RowTypography isSuccessor={!row[column.key]}>
					{formData[column.key]  ? formData[column.key] : ''}
				</RowTypography>
			</Stack>
        );
    }

    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
			<RowTypography isSuccessor={!row[column.key]}>
				{formData[column.key] ?? (row[column.key] || '')}
			</RowTypography>
		</Stack>
    );
};

const BulkUpdateConflictsModal: React.FC<BulkUpdateConflictsModalProps> = ({
	onTitleChange,
	onWidthChange,
	onCancel,
    bulkEditResponse,
    bulkSelectedRows,
    formData,
	queryPreference,
    handleSave,
	allocationType
}) => {
	const theme= useTheme();
	const colors = theme.palette;
	const isNestedTreeView = queryPreference.split('_')[0] === 'ResourceGroup';
	const defaultColumnNames = [ 'Sectors', 'Environment', 'Programs', 'Projects', 'Current Allocation %'];

	const columnNames = [
		{ name: 'sNo', label: 'S.No' },
		{ name: 'Resource', label: 'Resource' },
		{ name: 'PepwiseSector', label: 'Sectors' },
		{ name: 'PepwiseEnvironment', label: 'Environment' },
		{ name: 'PepwiseProgram', label: 'Programs' },
		{ name: 'PepwiseProject', label: 'Projects' },
		{ name: 'AllocPercent', label: 'Current Allocation %' }
	];

	const [resourceCount, setResourceCount] = useState<number>(0);
	const [parentRows, setParentRows] = useState<any[]>([]);

    useEffect(() => {
		if (bulkEditResponse?.data?.groupIds) {
			setResourceCount(bulkEditResponse.data.groupIds.length);
		}
	}, [bulkEditResponse]);

	useEffect(() => {
		// Conflicting item move to top
		const groupIds = bulkEditResponse?.data?.groupIds || [];
		if (isNestedTreeView) {
			const allRows = Object.values(bulkSelectedRows).filter((item: any) => item.isResource === true);

			const matchingRows = allRows.filter((item: any) => groupIds.includes(item.groupId));

			const nonMatchingRows = allRows.filter((item: any) => !groupIds.includes(item.groupId));
			const prioritizedRows = [...matchingRows, ...nonMatchingRows]
				.filter((item: any) => item)
				.map((item: any, index: number) => ({
					...item,
					sNo: index + 1, // Assign serial number
				}));
			// .flatMap(
			// 	(item: any) => {
			// 		if (isNestedTreeView) {
			// 			return item.values; // Flatten nested rows if needed
			// 		}
			// 		return item; // Keep flat structure
			// 	}
			// );

			setParentRows(prioritizedRows.filter((item: any) => item));
		} else {
			const allRows = Object.values(bulkSelectedRows);
			const matchingRows = allRows.filter((item: any) => groupIds.includes(item.groupId));
			const nonMatchingRows = allRows.filter((item: any) => !groupIds.includes(item.groupId));

			const prioritizedRows = [...matchingRows, ...nonMatchingRows]
				.flatMap((item: any) => (isNestedTreeView ? item.values : item))
				.filter((item: any) => item?.values)
				.map((item: any, index: number) => ({
					...item,
					sNo: index + 1, // Assign serial number
				}));
			setParentRows(prioritizedRows.filter((item: any) => item?.values));
		}
	}, [bulkSelectedRows, bulkEditResponse]);


    const filterBulkSelectedRows = (
			bulkSelectedRows: Record<string, { groupId: string; values: [] }>,
			bulkEditResponse: { data: { groupIds: string[] } }
		) => {
			const excludedGroupIds = new Set(bulkEditResponse.data.groupIds);
			// Filter out rows where the groupId matches any in the excludedGroupIds
			const filteredRows = Object.keys(bulkSelectedRows).reduce((acc, rowId) => {
				const row = bulkSelectedRows[rowId];
				// Check if row's groupId is not in excludedGroupIds
				if (!excludedGroupIds.has(row.groupId) && !row.values) {
					acc[rowId] = row;
				}
				return acc;
			}, {} as Record<string, { groupId: string }>);

			return filteredRows;
		};

        // cancel button handler
		const handleCancel = () => {
			onCancel();
		};
        // Force update button handler
		const handleForceUpdate = () => {
			handleSave(bulkSelectedRows, 'Y', 'N');
		};

        // resource conflict button handler
    const handleSkipConflict = () => {
        const filteredRows = filterBulkSelectedRows(bulkSelectedRows, bulkEditResponse);
        handleSave(filteredRows, 'N', 'Y');
    }

	const handleResourceClick = (resourceCount: any) => {
		//add logic for clicking the resourcecount
	};

	function getColumns(isAfterUpdate: boolean): Array<any> {
		return columnNames.map((col: any, idx: number) => {

			let columnKey = col.name;
			if (col.label === 'Current Allocation %' 
				|| (col.label === 'Current Allocation %' && isAfterUpdate)) {
				columnKey = 'AllocPercent';
			}

			return {
				key: columnKey,
				name: isAfterUpdate && col.label === 'Current Allocation %' ? 'Allocation %' : col.label,
				editable: false,
				formatter: pepwiseFormatter(isAfterUpdate, formData, colors),
				width: 168
			};
		});
	}
    
	return (
		<Stack
			spacing={2}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				px: 2,
				pb: 2,
				pt: 1,
				backgroundColor: `${colors.modal.bg} !important`,
				color: `${colors.modal.text} !important`
			}}
		>
			<Stack direction="row" alignItems="center" spacing={1}>
				<WarningImage className="warning-image" style={{ width: '4.5rem', height: '4.5rem' }} />

				<Stack
					sx={{
						alignItems: 'flex-start',
						justifyContent: 'center',
						pl: '1rem'
					}}
				>
					<Typography variant="pageTitle" sx={{ textAlign: 'left', pb: '0.5rem' }}>
						<Box component="span" sx={{ mr: 1 }}>
							{resourceCount}
						</Box>
						Conflicts Found
					</Typography>
					<Typography sx={{ textAlign: 'left'}}>
						Are you sure you want to force update and override the below{' '}
						<Box
							component="span"
							sx={{ color: 'info.main', cursor: 'pointer' }}
							onClick={() => handleResourceClick(resourceCount)}
						>
							{resourceCount} Resources
						</Box>{' '}
						to 100% allocation?
					</Typography>
				</Stack>
			</Stack>
			<Stack sx={{ flexDirection: 'column', gap: 1, textAlign: 'left' }}>
				<CustomizedDataGrid
					title="Allocation Before Update"
					tableColumns={getColumns(false)}
					tableData={parentRows}
					defaultColumnNames={defaultColumnNames}
					autoSetDraggedColumns={false}
					isRenderPagination={false}
					isShowMoreButton={false}
					style={{ maxHeight: '200px', marginBottom: '1rem' }}
				/>
				<CustomizedDataGrid
					title="Allocation After Update"
					tableColumns={getColumns(true)}
					tableData={parentRows}
					defaultColumnNames={defaultColumnNames}
					autoSetDraggedColumns={false}
					isRenderPagination={false}
					isShowMoreButton={false}
					style={{ maxHeight: '200px' }}
				/>
			</Stack>
			<Stack direction="row" justifyContent="space-between">
				<Button
					variant="pepwiseSecondaryOutline2"
					sx={{ color: `${colors.buttons.primary.color}`, width: '16rem' }}
					onClick={() => handleSkipConflict()}
					disabled={resourceCount===parentRows.length}
				>
					SKIP CONFLICT RESOURCES
				</Button>
				<Stack direction="row">
					<Button variant="pepwiseOutline" sx={{ width: '10rem', color: 'error.main' }} onClick={handleCancel}>
						CANCEL
					</Button>
					<Button
						sx={{
							...fontSizeResponsive(14),
							width: '10rem',
							backgroundColor: `${colors.error.main} !important`,
							color: '#FFFFFF !important',
							'&:hover': {
								backgroundColor: `${colors.error.main} !important`,
								color: '#FFFFFF !important',
							},
							'&.Mui-disabled': {
								backgroundColor: `${colors.error.main} !important`,
								color: '#FFFFFF !important',
								opacity: 0.5,
							},
						}}
						onClick={handleForceUpdate}
					>
						FORCE UPDATE
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default BulkUpdateConflictsModal;
